const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;
const cache = {};
// SphericalMercator constructor: precaches calculations
// for fast tile lookups.
class SphericalMercator {
    constructor({ size = 256, antimeridian = false, } = {}) {
        this.size = size;
        this.expansion = antimeridian === true ? 2 : 1;
        if (!cache[this.size]) {
            let size = this.size;
            let c = {
                Bc: [],
                Cc: [],
                zc: [],
                Ac: [],
            };
            cache[this.size] = c;
            for (let d = 0; d < 30; d++) {
                c.Bc.push(size / 360);
                c.Cc.push(size / (2 * Math.PI));
                c.zc.push(size / 2);
                c.Ac.push(size);
                size *= 2;
            }
        }
        this.Bc = cache[this.size].Bc;
        this.Cc = cache[this.size].Cc;
        this.zc = cache[this.size].zc;
        this.Ac = cache[this.size].Ac;
    }
    // Convert lon lat to screen pixel value
    //
    // - `ll` {Array} `[lon, lat]` array of geographic coordinates.
    // - `zoom` {Number} zoom level.
    px(ll, zoom) {
        if (!Number.isInteger(zoom)) {
            let size = this.size * Math.pow(2, zoom);
            let d = size / 2;
            let bc = size / 360;
            let cc = size / (2 * Math.PI);
            let ac = size;
            let f = Math.min(Math.max(Math.sin(D2R * ll[1]), -0.9999), 0.9999);
            let x = d + ll[0] * bc;
            let y = d + 0.5 * Math.log((1 + f) / (1 - f)) * -cc;
            x > ac * this.expansion && (x = ac * this.expansion);
            y > ac && (y = ac);
            //(x < 0) && (x = 0);
            //(y < 0) && (y = 0);
            return [x, y];
        }
        else {
            let d = this.zc[zoom];
            let f = Math.min(Math.max(Math.sin(D2R * ll[1]), -0.9999), 0.9999);
            let x = Math.round(d + ll[0] * this.Bc[zoom]);
            let y = Math.round(d + 0.5 * Math.log((1 + f) / (1 - f)) * -this.Cc[zoom]);
            x > this.Ac[zoom] * this.expansion &&
                (x = this.Ac[zoom] * this.expansion);
            y > this.Ac[zoom] && (y = this.Ac[zoom]);
            //(x < 0) && (x = 0);
            //(y < 0) && (y = 0);
            return [x, y];
        }
    }
    // Convert screen pixel value to lon lat
    //
    // - `px` {Array} `[x, y]` array of geographic coordinates.
    // - `zoom` {Number} zoom level.
    ll(px, zoom) {
        if (!Number.isInteger(zoom)) {
            let size = this.size * Math.pow(2, zoom);
            let bc = size / 360;
            let cc = size / (2 * Math.PI);
            let zc = size / 2;
            let g = (px[1] - zc) / -cc;
            let lon = (px[0] - zc) / bc;
            let lat = R2D * (2 * Math.atan(Math.exp(g)) - 0.5 * Math.PI);
            return [lon, lat];
        }
        else {
            let g = (px[1] - this.zc[zoom]) / -this.Cc[zoom];
            let lon = (px[0] - this.zc[zoom]) / this.Bc[zoom];
            let lat = R2D * (2 * Math.atan(Math.exp(g)) - 0.5 * Math.PI);
            return [lon, lat];
        }
    }
}

function getAdjusted(base, ratios, allowFloat) {
    const adjusted = Math.min(base - Math.log(ratios[0]) / Math.log(2), base - Math.log(ratios[1]) / Math.log(2));
    return allowFloat ? adjusted : Math.floor(adjusted);
}
/**
 * Given bounds in WSEN order and pixel dimensions,
 * figure out what center and zoom will produce a map
 * view with the same bounds and dimensions.
 */
function viewport(bounds, dimensions, { minzoom = 0, maxzoom = 20, tileSize = 256, allowFloat = false, allowAntiMeridian = false, } = {}) {
    const merc = new SphericalMercator({
        size: tileSize,
        antimeridian: allowAntiMeridian,
    });
    const base = maxzoom;
    const bl = merc.px([bounds[0], bounds[1]], base);
    const tr = merc.px([bounds[2], bounds[3]], base);
    const width = tr[0] - bl[0];
    const height = bl[1] - tr[1];
    const centerPixelX = bl[0] + width / 2;
    const centerPixelY = tr[1] + height / 2;
    const ratios = [width / dimensions[0], height / dimensions[1]];
    const adjusted = getAdjusted(base, ratios, allowFloat);
    const center = merc.ll([centerPixelX, centerPixelY], base);
    const zoom = Math.max(minzoom, Math.min(maxzoom, adjusted));
    return { center, zoom };
}
/**
 * The inverse of viewport: given center, zoom,
 * dimensions, and tileSize, return the bounds that will be produced
 * when you configure a map with those parameters.
 */
function bounds(center, zoom, dimensions, tileSize = 256) {
    if (!Array.isArray(center)) {
        center = [center.lon, center.lat];
    }
    const merc = new SphericalMercator({
        size: tileSize,
    });
    const px = merc.px(center, zoom);
    const tl = merc.ll([px[0] - dimensions[0] / 2, px[1] - dimensions[1] / 2], zoom);
    const br = merc.ll([px[0] + dimensions[0] / 2, px[1] + dimensions[1] / 2], zoom);
    return [tl[0], br[1], br[0], tl[1]];
}

export { bounds, viewport };
//# sourceMappingURL=geo-viewport.es.mjs.map
